import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft, ShieldCheck, ShieldAlert, Layers, Users, Rocket,
  History, ListChecks, User, BarChart3, Gift, Award, Trophy, TrendingUp,
  Building2, FileText, Bell, KeyRound, HelpCircle, ScrollText, Lock,
  Info, Coins, ChevronRight, Loader2, Plus, LogIn, Wallet,
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/* ------------------------------------------------------------- helpers */
const rupees = (paise) => {
  if (paise == null) return "—";
  const rs = paise / 100;
  return rs.toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 });
};
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

/* ------------------------------------------------------------- tabs */
const TABS = [
  { id: "home",       label: "Home",         icon: Rocket },
  { id: "explore",    label: "Explore",      icon: Layers },
  { id: "application",label: "Application",  icon: FileText },
  { id: "active",     label: "Active",       icon: Users },
  { id: "history",    label: "History",      icon: History },
  { id: "txn",        label: "Transactions", icon: ListChecks },
  { id: "refer",      label: "Refer & Earn", icon: Gift },
  { id: "leaderboard",label: "Leaderboard",  icon: Trophy },
  { id: "top",        label: "Top",          icon: Award },
  { id: "bank",       label: "Bank",         icon: Building2 },
  { id: "profile",    label: "Profile",      icon: User },
  { id: "stats",      label: "Statistics",   icon: BarChart3 },
  { id: "admin",      label: "Admin",        icon: KeyRound },
  { id: "more",       label: "More",         icon: Info },
];

const COMING_SOON = [
  { label: "Bulk Invitations",       icon: Users },
  { label: "Documents",              icon: FileText },
  { label: "Notifications",          icon: Bell },
  { label: "Security / PIN",         icon: Lock },
  { label: "Help",                   icon: HelpCircle },
  { label: "Terms & Conditions",    icon: ScrollText },
  { label: "Privacy Policy",         icon: ScrollText },
  { label: "Transactional Analytics", icon: BarChart3 },
  { label: "Leverage Analytics",     icon: TrendingUp },
  { label: "Earnings Analytics",     icon: Coins },
];

/* ------------------------------------------------------------- main */
export default function EFP() {
  const nav = useNavigate();
  const [tab, setTab] = useState("home");
  const [home, setHome] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadHome = useCallback(async () => {
    setBusy(true);
    try {
      const r = await api.get("/efp/home");
      setHome(r.data);
    } catch (e) {
      if (e?.response?.status === 401) { setHome({ unauthenticated: true }); return; }
      toast.error("Couldn't load EFP");
    } finally { setBusy(false); }
  }, []);

  useEffect(() => { loadHome(); }, [loadHome]);

  return (
    <div className="min-h-screen bg-stone-50 pb-24" data-testid="efp-root">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white border-b border-stone-200">
        <div className="max-w-5xl mx-auto flex items-center gap-2 px-3 py-2">
          <button onClick={() => nav(-1)} data-testid="efp-back" className="p-1.5 rounded-full hover:bg-stone-100"><ArrowLeft size={18}/></button>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase font-black tracking-widest text-amber-700">EFP</div>
            <div className="font-black text-sm text-stone-900 truncate">Elite Fund Pool</div>
          </div>
        </div>
        <div className="max-w-5xl mx-auto px-3 pb-2 flex gap-1.5 overflow-x-auto no-scrollbar">
          {TABS.map((t) => {
            const Ic = t.icon;
            return (
              <button key={t.id} onClick={() => setTab(t.id)} data-testid={`efp-tab-${t.id}`}
                      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-black uppercase whitespace-nowrap ${
                        tab === t.id ? "bg-amber-600 text-white" : "bg-stone-100 text-stone-600"
                      }`}>
                <Ic size={11}/> {t.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="max-w-5xl mx-auto p-3 space-y-3">
        {busy && !home && (
          <div className="py-16 text-center text-stone-500">
            <Loader2 size={20} className="animate-spin inline-block mr-1.5"/> Loading…
          </div>
        )}
        {tab === "home"        && home?.unauthenticated && <NeedLogin/>}
        {tab === "home"        && home && !home.unauthenticated && <HomeTab home={home} setTab={setTab}/>}
        {tab === "explore"     && <ExploreTab reloadHome={loadHome}/>}
        {tab === "application" && <ApplicationTab reloadHome={loadHome}/>}
        {tab === "active"      && <ActivePoolsTab/>}
        {tab === "history"     && <HistoryTab/>}
        {tab === "txn"         && <TransactionsTab/>}
        {tab === "refer"       && <ReferTab/>}
        {tab === "leaderboard" && <LeaderboardTab/>}
        {tab === "top"         && <TopContributorsTab/>}
        {tab === "bank"        && <BankTab/>}
        {tab === "profile"     && <ProfileTab/>}
        {tab === "stats"       && <StatsTab/>}
        {tab === "admin"       && <AdminTab/>}
        {tab === "more"        && <MoreTab/>}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------- Home tab */
function HomeTab({ home, setTab }) {
  const e = home.eligibility;
  return (
    <>
      <div className="rounded-2xl bg-gradient-to-br from-amber-600 via-orange-600 to-rose-600 text-white p-4" data-testid="efp-hero">
        <div className="text-[11px] uppercase font-black tracking-widest opacity-90">Elite Fund Pool</div>
        <div className="font-black text-lg mt-0.5">Verified users. Pooled capital.</div>
        <div className="text-[11px] opacity-90 mt-0.5">Find2near KYC · Native ledger · Zero duplicate identity.</div>
        <div className="mt-3 text-[10px] uppercase font-black opacity-90 bg-white/15 inline-block px-2 py-0.5 rounded-full">
          Payments: {home.provider === "mock" ? "MOCK ONLY" : home.provider}
        </div>
      </div>

      {/* Eligibility card */}
      <div className="bg-white rounded-2xl border border-stone-200 p-3" data-testid="efp-eligibility">
        <div className="flex items-center gap-2">
          {e.eligible ? (
            <ShieldCheck size={18} className="text-emerald-600"/>
          ) : (
            <ShieldAlert size={18} className="text-amber-600"/>
          )}
          <div className="flex-1">
            <div className="text-[10px] uppercase font-black text-stone-500">Eligibility</div>
            <div className="font-black text-sm">
              {e.eligible ? "You are eligible to join EFP pools" : "Complete the checklist below"}
            </div>
          </div>
        </div>
        <div className="mt-2 grid grid-cols-2 gap-2 text-[11px]">
          <StatusRow label="Find2near KYC" ok={e.kyc_verified} value={e.kyc_status}/>
          <StatusRow label="EFP Application" ok={e.application_status === "approved"} value={e.application_status}/>
        </div>
        {e.reasons?.length > 0 && (
          <ul className="mt-2 text-[11px] text-amber-700 list-disc list-inside">
            {e.reasons.map((r, i) => <li key={i}>{r}</li>)}
          </ul>
        )}
        {!e.kyc_verified && (
          <Link to="/kyc" data-testid="efp-goto-kyc"
                className="mt-2 inline-flex items-center gap-1 text-[11px] font-black text-indigo-600">
            Go to Find2near KYC <ChevronRight size={12}/>
          </Link>
        )}
        {e.kyc_verified && e.application_status !== "approved" && (
          <button onClick={() => setTab("application")} data-testid="efp-goto-app"
                  className="mt-2 inline-flex items-center gap-1 text-[11px] font-black text-indigo-600">
            Complete EFP application <ChevronRight size={12}/>
          </button>
        )}
      </div>

      {/* Counts */}
      <div className="grid grid-cols-3 gap-2">
        <CountCard label="Active" value={home.active_pools} onClick={() => setTab("active")}/>
        <CountCard label="History" value={home.historical_pools} onClick={() => setTab("history")}/>
        <CountCard label="Transactions" value={home.transactions} onClick={() => setTab("txn")}/>
      </div>

      <div className="text-[11px] text-stone-500 bg-stone-100 rounded-xl p-2 italic">{home.disclaimer}</div>
    </>
  );
}

function StatusRow({ label, ok, value }) {
  return (
    <div className={`rounded-lg p-2 border ${ok ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`}>
      <div className="text-[9px] uppercase font-black text-stone-500">{label}</div>
      <div className={`font-black text-[11px] ${ok ? "text-emerald-700" : "text-amber-800"}`}>{value}</div>
    </div>
  );
}

function CountCard({ label, value, onClick }) {
  return (
    <button onClick={onClick} data-testid={`efp-count-${label.toLowerCase()}`}
            className="bg-white border border-stone-200 rounded-xl p-2 text-left hover:border-amber-300">
      <div className="text-[9px] uppercase font-black text-stone-500">{label}</div>
      <div className="font-black text-xl text-stone-900">{value ?? 0}</div>
    </button>
  );
}

/* ------------------------------------------------------------- Application */
function ApplicationTab({ reloadHome }) {
  const [me, setMe] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ display_name: "", city: "", monthly_capacity_rupees: "", referral_code: "", accept_terms: false });

  const load = useCallback(async () => {
    const r = await api.get("/efp/application/me");
    setMe(r.data);
    if (r.data?.application) {
      setForm((s) => ({
        ...s,
        display_name: r.data.application.display_name || "",
        city: r.data.application.city || "",
        monthly_capacity_rupees: r.data.application.monthly_capacity_paise
          ? String(Math.round(r.data.application.monthly_capacity_paise / 100)) : "",
        referral_code: r.data.application.referral_code || "",
      }));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.display_name.trim()) { toast.error("Display name required"); return; }
    if (!form.accept_terms) { toast.error("Accept terms to proceed"); return; }
    setBusy(true);
    try {
      const monthly_capacity_paise = form.monthly_capacity_rupees
        ? Math.round(Number(form.monthly_capacity_rupees) * 100) : null;
      const r = await api.post("/efp/application", {
        display_name: form.display_name.trim(),
        city: form.city.trim() || null,
        monthly_capacity_paise,
        referral_code: form.referral_code.trim() || null,
        accept_terms: true,
      });
      toast.success("Application submitted");
      setMe({ application: r.data.application, eligibility: r.data.eligibility });
      reloadHome && reloadHome();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to submit");
    } finally { setBusy(false); }
  };

  if (!me) return <Loader/>;
  const app = me.application;
  return (
    <div className="space-y-2" data-testid="efp-app">
      <div className="bg-white rounded-2xl border border-stone-200 p-3">
        <div className="text-[10px] uppercase font-black text-stone-500">EFP Application</div>
        <div className="text-[11px] text-stone-600 mb-2">Find2near KYC is reused — you will NOT be asked to re-submit KYC.</div>
        {app?.status === "approved" && (
          <div className="text-[11px] text-emerald-700 font-black bg-emerald-50 border border-emerald-200 rounded-lg p-2 mb-2">
            ✓ Application approved on {fmtDate(app.approved_at)}
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <Field label="Display name">
            <input value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                   data-testid="efp-app-name" className="w-full px-2 py-1.5 rounded-lg border border-stone-200 text-sm"/>
          </Field>
          <Field label="City">
            <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
                   data-testid="efp-app-city" className="w-full px-2 py-1.5 rounded-lg border border-stone-200 text-sm"/>
          </Field>
          <Field label="Monthly capacity (₹, optional)">
            <input type="number" value={form.monthly_capacity_rupees}
                   onChange={(e) => setForm({ ...form, monthly_capacity_rupees: e.target.value })}
                   data-testid="efp-app-capacity" className="w-full px-2 py-1.5 rounded-lg border border-stone-200 text-sm"/>
          </Field>
          <Field label="Referral code (optional)">
            <input value={form.referral_code} onChange={(e) => setForm({ ...form, referral_code: e.target.value })}
                   data-testid="efp-app-ref" className="w-full px-2 py-1.5 rounded-lg border border-stone-200 text-sm"/>
          </Field>
        </div>
        <label className="flex items-center gap-2 mt-2 text-[11px] cursor-pointer">
          <input type="checkbox" checked={form.accept_terms}
                 onChange={(e) => setForm({ ...form, accept_terms: e.target.checked })}
                 data-testid="efp-app-terms"/>
          I accept the EFP Terms & Conditions and understand payments are in MOCK mode.
        </label>
        <button onClick={submit} disabled={busy} data-testid="efp-app-submit"
                className="mt-2 w-full py-2 rounded-full bg-amber-600 text-white font-black text-sm disabled:opacity-50">
          {busy ? <Loader2 size={14} className="animate-spin inline mr-1"/> : null}
          {app ? "Update application" : "Submit application"}
        </button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[9px] uppercase font-black text-stone-500">{label}</span>
      {children}
    </label>
  );
}

/* ------------------------------------------------------------- Explore */
function ExploreTab({ reloadHome }) {
  const [data, setData] = useState(null);
  const [joining, setJoining] = useState(null);
  const load = useCallback(async () => {
    const r = await api.get("/efp/pools/explore");
    setData(r.data);
  }, []);
  useEffect(() => { load(); }, [load]);
  const join = async (pool_id) => {
    setJoining(pool_id);
    try {
      await api.post(`/efp/pools/${pool_id}/join`, {});
      toast.success("Joined pool");
      load();
      reloadHome && reloadHome();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.detail || e?.response?.data?.detail || "Could not join");
    } finally { setJoining(null); }
  };
  if (!data) return <Loader/>;
  if (!data.eligibility?.eligible) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-[12px] text-amber-800" data-testid="efp-explore-blocked">
        <div className="font-black">Not yet eligible</div>
        <div>{data.hint}</div>
        <ul className="list-disc list-inside mt-1">{(data.eligibility.reasons || []).map((r, i) => <li key={i}>{r}</li>)}</ul>
      </div>
    );
  }
  return (
    <div className="space-y-2" data-testid="efp-explore">
      {data.items.length === 0 && (
        <div className="p-4 text-center text-stone-500 text-xs italic bg-white border border-stone-200 rounded-xl">
          No open pools right now. Ask your admin to create a pool type + open a pool.
        </div>
      )}
      {data.items.map((p) => (
        <div key={p.pool_id} className="bg-white border border-stone-200 rounded-2xl p-3"
             data-testid={`efp-pool-${p.pool_id}`}>
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase font-black text-stone-500">{p.pool_type_name}</div>
              <div className="font-black text-sm text-stone-900">{p.name}</div>
            </div>
            <span className="text-[9px] font-black px-1.5 py-0.5 rounded-full bg-emerald-600 text-white">{p.status}</span>
          </div>
          <div className="grid grid-cols-3 gap-2 mt-2 text-[11px]">
            <Mini label="Contribution" value={rupees(p.contribution_paise)}/>
            <Mini label="Cycle" value={p.cycle}/>
            <Mini label="Seats" value={`${p.seats_left}/${p.total_members}`}/>
          </div>
          <button onClick={() => join(p.pool_id)} disabled={joining === p.pool_id}
                  data-testid={`efp-join-${p.pool_id}`}
                  className="mt-2 w-full py-1.5 rounded-full bg-amber-600 text-white font-black text-xs disabled:opacity-50">
            {joining === p.pool_id ? <Loader2 size={12} className="animate-spin inline mr-1"/> : <LogIn size={12} className="inline mr-1"/>}
            Join pool
          </button>
        </div>
      ))}
    </div>
  );
}

function Mini({ label, value }) {
  return (
    <div className="bg-stone-50 rounded-lg p-1.5">
      <div className="text-[9px] uppercase font-black text-stone-500">{label}</div>
      <div className="font-black text-[11px] text-stone-900">{value}</div>
    </div>
  );
}

/* ------------------------------------------------------------- Active */
function ActivePoolsTab() {
  const [items, setItems] = useState([]);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    const r = await api.get("/efp/pools/active");
    setItems(r.data.items || []);
  }, []);
  useEffect(() => { load(); }, [load]);
  const openDetail = async (pool_id) => {
    const r = await api.get(`/efp/pools/${pool_id}`);
    setDetail(r.data);
  };
  const contribute = async (pool_id) => {
    setBusy(true);
    try {
      const r = await api.post(`/efp/pools/${pool_id}/contribute`, {});
      toast.success(`Contribution posted (mock): ${rupees(r.data.ledger.amount_paise)}`);
      openDetail(pool_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };
  if (detail) {
    const p = detail.pool;
    return (
      <div className="space-y-2" data-testid="efp-active-detail">
        <button onClick={() => setDetail(null)} className="text-xs text-stone-500 font-black">← Back</button>
        <div className="bg-white border border-stone-200 rounded-2xl p-3">
          <div className="text-[10px] uppercase font-black text-stone-500">{p.pool_type_name}</div>
          <div className="font-black text-sm">{p.name}</div>
          <div className="grid grid-cols-3 gap-2 mt-2 text-[11px]">
            <Mini label="Contribution" value={rupees(p.contribution_paise)}/>
            <Mini label="Members" value={`${p.member_count}/${p.total_members}`}/>
            <Mini label="Status" value={p.status}/>
          </div>
          <div className="grid grid-cols-3 gap-2 mt-2 text-[11px]">
            <Mini label="Total in" value={rupees(detail.totals.contributions_paise)}/>
            <Mini label="Total out" value={rupees(detail.totals.payouts_paise)}/>
            <Mini label="Fees" value={rupees(detail.totals.fees_paise)}/>
          </div>
          {p.status !== "closed" && p.status !== "cancelled" && p.status !== "defaulted" && (
            <button onClick={() => contribute(p.pool_id)} disabled={busy}
                    data-testid="efp-contribute-btn"
                    className="mt-3 w-full py-2 rounded-full bg-amber-600 text-white font-black text-xs disabled:opacity-50">
              {busy ? <Loader2 size={12} className="animate-spin inline mr-1"/> : <Wallet size={12} className="inline mr-1"/>}
              Contribute {rupees(p.contribution_paise)} (mock)
            </button>
          )}
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-2" data-testid="efp-active">
      {items.length === 0 && (
        <div className="p-4 text-center text-stone-500 text-xs italic bg-white border border-stone-200 rounded-xl">
          You have no active EFP pools.
        </div>
      )}
      {items.map(({ membership, pool }) => (
        <button key={membership.membership_id} onClick={() => openDetail(pool.pool_id)}
                data-testid={`efp-active-${pool.pool_id}`}
                className="w-full text-left bg-white border border-stone-200 rounded-2xl p-3">
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <div className="text-[10px] uppercase font-black text-stone-500">{pool.pool_type_name}</div>
              <div className="font-black text-sm text-stone-900 truncate">{pool.name}</div>
            </div>
            <span className="text-[9px] font-black px-1.5 py-0.5 rounded-full bg-emerald-600 text-white">{pool.status}</span>
          </div>
          <div className="grid grid-cols-3 gap-2 mt-2 text-[11px]">
            <Mini label="Contribution" value={rupees(pool.contribution_paise)}/>
            <Mini label="My contribs" value={rupees(membership.contributed_paise)}/>
            <Mini label="Members" value={`${pool.member_count}/${pool.total_members}`}/>
          </div>
        </button>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------- History */
function HistoryTab() {
  const [status, setStatus] = useState("");
  const [items, setItems] = useState([]);
  const load = useCallback(async () => {
    const q = status ? `?status=${status}` : "";
    const r = await api.get(`/efp/pools/history${q}`);
    setItems(r.data.items || []);
  }, [status]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-2" data-testid="efp-history">
      <div className="flex gap-1">
        {["", "closed", "cancelled", "defaulted"].map((s) => (
          <button key={s || "all"} onClick={() => setStatus(s)}
                  data-testid={`efp-history-tab-${s || "all"}`}
                  className={`px-2.5 py-1 rounded-full text-[11px] font-black uppercase ${
                    status === s ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-600"}`}>
            {s || "All"}
          </button>
        ))}
      </div>
      {items.length === 0 && (
        <div className="p-4 text-center text-stone-500 text-xs italic bg-white border border-stone-200 rounded-xl">
          No historical EFP pools
        </div>
      )}
      {items.map(({ membership, pool }) => (
        <div key={membership.membership_id} className="bg-white border border-stone-200 rounded-2xl p-3"
             data-testid={`efp-history-${pool.pool_id}`}>
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase font-black text-stone-500">{pool.pool_type_name}</div>
              <div className="font-black text-sm text-stone-900">{pool.name}</div>
            </div>
            <span className={`text-[9px] font-black px-1.5 py-0.5 rounded-full text-white ${
              membership.status === "closed" ? "bg-stone-500" :
              membership.status === "cancelled" ? "bg-amber-600" : "bg-rose-600"}`}>
              {membership.status}
            </span>
          </div>
          <div className="grid grid-cols-3 gap-2 mt-2 text-[11px]">
            <Mini label="Contribs" value={rupees(membership.contributed_paise)}/>
            <Mini label="Payouts" value={rupees(membership.payout_paise)}/>
            <Mini label="Ended" value={fmtDate(membership.closed_at)}/>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------- Transactions */
function TransactionsTab() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/efp/transactions").then((r) => setItems(r.data.items || [])).catch(() => {});
  }, []);
  return (
    <div className="space-y-2" data-testid="efp-txn">
      {items.length === 0 && (
        <div className="p-4 text-center text-stone-500 text-xs italic bg-white border border-stone-200 rounded-xl">
          No transactions yet.
        </div>
      )}
      {items.map((t) => (
        <div key={t.ledger_id} className="bg-white border border-stone-200 rounded-xl p-2 flex items-center gap-2"
             data-testid={`efp-txn-${t.ledger_id}`}>
          <div className={`w-1.5 h-8 rounded-full ${t.direction === "debit" ? "bg-rose-500" : "bg-emerald-500"}`}/>
          <div className="flex-1 min-w-0">
            <div className="font-black text-[12px] text-stone-900">{t.note}</div>
            <div className="text-[10px] text-stone-500">{fmtDate(t.at)} · {t.provider}</div>
          </div>
          <div className={`font-black text-[13px] ${t.direction === "debit" ? "text-rose-600" : "text-emerald-600"}`}>
            {t.direction === "debit" ? "−" : "+"}{rupees(t.amount_paise)}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------- Refer & Earn */
function ReferTab() {
  const [data, setData] = useState(null);
  const [qr, setQr] = useState(null);
  const [contacts, setContacts] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try {
      const r = await api.get("/efp/referral/me");
      setData(r.data);
    } catch { /* ignored */ }
  }, []);
  const loadQr = useCallback(async () => {
    try {
      const r = await api.get("/efp/referral/qr");
      setQr(r.data);
    } catch { /* ignored */ }
  }, []);
  useEffect(() => { load(); loadQr(); }, [load, loadQr]);
  if (!data) return <Loader/>;
  const share = () => {
    const msg = data.share_message;
    const url = `https://wa.me/?text=${encodeURIComponent(msg)}`;
    window.open(url, "_blank");
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(data.invite_code); toast.success("Code copied"); }
    catch { toast.error("Copy failed"); }
  };
  const submitContacts = async () => {
    const lines = contacts.split(/[\n,]+/).map(s => s.trim()).filter(Boolean);
    const parsed = lines.map(l => {
      const parts = l.split(/\s*[|]\s*/);
      const [name, phone, email] = [parts[0] || "", parts[1] || "", parts[2] || ""];
      // heuristics if user didn't use pipes
      if (!phone && !email) {
        if (/@/.test(name)) return { name: "", email: name };
        if (/^[+\d]/.test(name)) return { name: "", phone: name };
      }
      return { name, phone, email };
    });
    if (!parsed.length) { toast.error("Add at least one contact"); return; }
    setBusy(true);
    try {
      const r = await api.post("/efp/referral/bulk-invite", { contacts: parsed });
      toast.success(`Recorded ${r.data.recorded} invitations`);
      setContacts("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="space-y-2" data-testid="efp-refer">
      <div className="bg-gradient-to-br from-amber-500 via-orange-500 to-rose-500 text-white rounded-2xl p-4">
        <div className="text-[10px] uppercase font-black opacity-90 tracking-widest">Your invite code</div>
        <div className="font-black text-2xl tracking-wider mt-1" data-testid="efp-invite-code">{data.invite_code}</div>
        <div className="text-[11px] opacity-90 mt-1">{data.share_message}</div>
        <div className="flex flex-wrap gap-1.5 mt-3">
          <button onClick={copy} data-testid="efp-copy-code"
                  className="px-3 py-1.5 rounded-full bg-white/20 text-[11px] font-black uppercase">Copy code</button>
          <button onClick={share} data-testid="efp-share-wa"
                  className="px-3 py-1.5 rounded-full bg-white/20 text-[11px] font-black uppercase">Share on WhatsApp</button>
          <button onClick={async () => {
            try {
              if (navigator.share) { await navigator.share({ text: data.share_message }); }
              else { await navigator.clipboard.writeText(data.share_message); toast.success("Copied to clipboard"); }
            } catch { /* ignored */ }
          }} data-testid="efp-share-any"
                  className="px-3 py-1.5 rounded-full bg-white/20 text-[11px] font-black uppercase">Share</button>
        </div>
      </div>

      {qr?.data_url && (
        <div className="bg-white border border-stone-200 rounded-2xl p-3 flex items-center gap-3"
             data-testid="efp-qr">
          <img src={qr.data_url} alt="QR" className="w-24 h-24 rounded-lg border border-stone-200 bg-white"/>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase font-black text-stone-500">QR invite</div>
            <div className="text-[11px] text-stone-600">Scan with any camera → opens EFP invite.</div>
            <div className="text-[10px] font-mono text-stone-400 mt-1 truncate">{qr.payload}</div>
          </div>
        </div>
      )}

      {/* Funnel */}
      <div className="bg-white border border-stone-200 rounded-2xl p-3" data-testid="efp-funnel">
        <div className="text-[10px] uppercase font-black text-stone-500">Referral funnel</div>
        <div className="grid grid-cols-7 gap-1 mt-2">
          {["invited","registered","kyc","eligible","joined","qualified","rewarded"].map((s) => (
            <div key={s} className="bg-stone-50 rounded-lg py-1.5 text-center">
              <div className="font-black text-sm">{data.funnel[s] || 0}</div>
              <div className="text-[8.5px] uppercase font-black text-stone-500">{s}</div>
            </div>
          ))}
        </div>
        <div className="mt-2 text-[11px] flex items-center justify-between">
          <div>Reward per qualified referral</div>
          <div className="font-black text-stone-900">{rupees(data.reward_per_qualified_paise)}</div>
        </div>
        <div className="mt-1 text-[11px] flex items-center justify-between">
          <div>Rewards earned (ledger)</div>
          <div className="font-black text-emerald-600">{rupees(data.rewards_paise)}</div>
        </div>
        <div className="text-[10px] italic text-stone-500 mt-1">{data.disclaimer}</div>
      </div>

      {/* Bulk invite */}
      <div className="bg-white border border-stone-200 rounded-2xl p-3">
        <div className="text-[10px] uppercase font-black text-stone-500 mb-1">Bulk contact invitations</div>
        <div className="text-[11px] text-stone-500 mb-1">
          One contact per line: <span className="font-mono">Name | Phone | Email</span> (any of phone/email)
        </div>
        <textarea rows={4} value={contacts} onChange={(e) => setContacts(e.target.value)}
                  data-testid="efp-bulk-contacts"
                  className="w-full px-2 py-1.5 rounded-lg border border-stone-200 text-sm"
                  placeholder="Amit | 9876543210&#10;Riya | | riya@example.com"/>
        <button onClick={submitContacts} disabled={busy} data-testid="efp-bulk-send"
                className="mt-2 w-full py-2 rounded-full bg-amber-600 text-white font-black text-xs disabled:opacity-50">
          {busy ? <Loader2 size={12} className="animate-spin inline mr-1"/> : null}
          Record invitations
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------- Leaderboard */
function LeaderboardTab() {
  const [period, setPeriod] = useState("month");
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    const r = await api.get(`/efp/leaderboard?period=${period}`);
    setData(r.data);
  }, [period]);
  useEffect(() => { load(); }, [load]);
  const countdown = useMemo(() => {
    if (!data?.countdown_seconds_left) return null;
    const s = data.countdown_seconds_left;
    const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60);
    return `${d}d ${h}h ${m}m`;
  }, [data]);
  return (
    <div className="space-y-2" data-testid="efp-leaderboard">
      <div className="flex gap-1">
        {[["month","This Month"],["quarter","This Quarter"],["all","All Time"]].map(([id, label]) => (
          <button key={id} onClick={() => setPeriod(id)}
                  data-testid={`efp-lb-tab-${id}`}
                  className={`px-2.5 py-1 rounded-full text-[11px] font-black uppercase ${
                    period === id ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-600"}`}>
            {label}
          </button>
        ))}
        {countdown && period !== "all" && (
          <span className="ml-auto px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 text-[11px] font-black uppercase"
                data-testid="efp-lb-countdown">Ends in {countdown}</span>
        )}
      </div>
      {data?.prizes?.length > 0 && (
        <div className="bg-white border border-stone-200 rounded-2xl p-3">
          <div className="text-[10px] uppercase font-black text-stone-500 mb-1">Prize slots</div>
          {data.prizes.map((p) => (
            <div key={p.prize_id} className="flex items-center justify-between text-[11px] py-0.5"
                 data-testid={`efp-prize-${p.prize_id}`}>
              <div>#{p.rank} · {p.title}</div>
              <div className="font-black">{p.reward_paise ? rupees(p.reward_paise) : (p.description || "—")}</div>
            </div>
          ))}
        </div>
      )}
      {(!data || data.items?.length === 0) && (
        <div className="p-4 text-center text-stone-500 text-xs italic bg-white border border-stone-200 rounded-xl">
          No qualified referrals yet. Be the first!
        </div>
      )}
      {data?.items?.map((row) => (
        <div key={row.user_id} className="bg-white border border-stone-200 rounded-xl p-2 flex items-center gap-2"
             data-testid={`efp-lb-row-${row.rank}`}>
          <div className={`w-8 h-8 rounded-full grid place-items-center font-black text-white ${
            row.rank === 1 ? "bg-amber-500" : row.rank === 2 ? "bg-stone-400" : row.rank === 3 ? "bg-orange-600" : "bg-stone-300"
          }`}>{row.rank}</div>
          <div className="flex-1 min-w-0">
            <div className="font-black text-[12px] text-stone-900 truncate">{row.display_name}</div>
          </div>
          <div className="text-[11px] font-black text-stone-700">{row.qualified_referrals} refs</div>
        </div>
      ))}
      {data && <div className="text-[10px] italic text-stone-500">{data.disclaimer}</div>}
    </div>
  );
}

/* ------------------------------------------------------------- Top Contributors */
function TopContributorsTab() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/efp/top-contributors").then((r) => setItems(r.data.items || [])).catch(() => {});
  }, []);
  return (
    <div className="space-y-2" data-testid="efp-top">
      {items.length === 0 && (
        <div className="p-4 text-center text-stone-500 text-xs italic bg-white border border-stone-200 rounded-xl">
          No contributors yet.
        </div>
      )}
      {items.map((r, i) => (
        <div key={r.user_id} className="bg-white border border-stone-200 rounded-xl p-2 flex items-center gap-2">
          <div className={`w-7 h-7 rounded-full grid place-items-center font-black text-white text-[11px] ${
            i === 0 ? "bg-amber-500" : i === 1 ? "bg-stone-400" : i === 2 ? "bg-orange-600" : "bg-stone-300"}`}>{i + 1}</div>
          <div className="flex-1 min-w-0">
            <div className="font-black text-[12px] text-stone-900 truncate">{r.display_name}</div>
            <div className="text-[10px] text-stone-500">{r.n_contributions} contributions</div>
          </div>
          <div className="font-black text-[12px] text-stone-900">{rupees(r.total_contributed_paise)}</div>
        </div>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------- Bank Accounts */
function BankTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ account_number: "", ifsc: "", holder_name: "" });
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    const r = await api.get("/efp/bank-accounts");
    setItems(r.data.items || []);
  }, []);
  useEffect(() => { load(); }, [load]);
  const add = async () => {
    setBusy(true);
    try {
      await api.post("/efp/bank-accounts", form);
      toast.success("Bank account added");
      setForm({ account_number: "", ifsc: "", holder_name: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };
  const setPrimary = async (bank_id) => {
    try { await api.post(`/efp/bank-accounts/${bank_id}/set-primary`); load(); toast.success("Primary set"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const del = async (bank_id) => {
    if (!window.confirm("Remove this bank account?")) return;
    try { await api.delete(`/efp/bank-accounts/${bank_id}`); load(); toast.success("Removed"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="space-y-2" data-testid="efp-bank">
      <div className="bg-white border border-stone-200 rounded-2xl p-3">
        <div className="text-[10px] uppercase font-black text-stone-500">Add bank account</div>
        <div className="text-[11px] text-stone-500 mb-2">
          For payout readiness. No live money moves until GO LIVE.
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <Field label="Account holder">
            <input value={form.holder_name} onChange={(e) => setForm({ ...form, holder_name: e.target.value })}
                   data-testid="efp-bank-holder" className="w-full px-2 py-1.5 rounded-lg border border-stone-200 text-sm"/>
          </Field>
          <Field label="Account number">
            <input value={form.account_number} onChange={(e) => setForm({ ...form, account_number: e.target.value })}
                   data-testid="efp-bank-acc" className="w-full px-2 py-1.5 rounded-lg border border-stone-200 text-sm"/>
          </Field>
          <Field label="IFSC">
            <input value={form.ifsc} onChange={(e) => setForm({ ...form, ifsc: e.target.value.toUpperCase() })}
                   data-testid="efp-bank-ifsc" className="w-full px-2 py-1.5 rounded-lg border border-stone-200 text-sm"/>
          </Field>
        </div>
        <button onClick={add} disabled={busy} data-testid="efp-bank-add"
                className="mt-2 w-full py-2 rounded-full bg-amber-600 text-white font-black text-xs disabled:opacity-50">
          {busy ? <Loader2 size={12} className="animate-spin inline mr-1"/> : null}
          Add bank account
        </button>
      </div>

      {items.length === 0 && (
        <div className="p-4 text-center text-stone-500 text-xs italic bg-white border border-stone-200 rounded-xl">
          No bank accounts saved.
        </div>
      )}
      {items.map((b) => (
        <div key={b.bank_id} className="bg-white border border-stone-200 rounded-2xl p-3"
             data-testid={`efp-bank-row-${b.bank_id}`}>
          <div className="flex items-center justify-between">
            <div>
              <div className="font-black text-sm">{b.holder_name}</div>
              <div className="text-[11px] text-stone-500 font-mono">{b.account_number_masked} · {b.ifsc}</div>
            </div>
            <div className="flex flex-col items-end gap-1">
              {b.is_primary && <span className="text-[9px] font-black px-1.5 py-0.5 rounded-full bg-emerald-600 text-white">PRIMARY</span>}
              {b.verified ? (
                <span className="text-[9px] font-black px-1.5 py-0.5 rounded-full bg-indigo-600 text-white">VERIFIED</span>
              ) : (
                <span className="text-[9px] font-black px-1.5 py-0.5 rounded-full bg-amber-500 text-white">UNVERIFIED</span>
              )}
            </div>
          </div>
          <div className="flex gap-1 mt-2">
            {!b.is_primary && (
              <button onClick={() => setPrimary(b.bank_id)} data-testid={`efp-bank-primary-${b.bank_id}`}
                      className="text-[10px] px-2 py-1 rounded-full bg-stone-900 text-white font-black uppercase">Set primary</button>
            )}
            <button onClick={() => del(b.bank_id)} data-testid={`efp-bank-del-${b.bank_id}`}
                    className="text-[10px] px-2 py-1 rounded-full bg-rose-600 text-white font-black uppercase">Remove</button>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------- Profile */
function ProfileTab() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/efp/profile").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return <Loader/>;
  const r = data.registered || {};
  return (
    <div className="space-y-2" data-testid="efp-profile">
      <div className="bg-white border border-stone-200 rounded-2xl p-3">
        <div className="text-[10px] uppercase font-black text-stone-500 mb-1">Registered</div>
        <Row k="Name" v={r.name}/> <Row k="Email" v={r.email}/> <Row k="Mobile" v={r.mobile}/>
        <Row k="KYC" v={r.kyc_status}/> <Row k="Member since" v={fmtDate(r.created_at)}/>
      </div>
      {data.application && (
        <div className="bg-white border border-stone-200 rounded-2xl p-3">
          <div className="text-[10px] uppercase font-black text-stone-500 mb-1">Self-declared (EFP)</div>
          <Row k="Display name" v={data.application.display_name}/>
          <Row k="City" v={data.application.city}/>
          <Row k="Monthly capacity" v={data.application.monthly_capacity_paise ? rupees(data.application.monthly_capacity_paise) : "—"}/>
          <Row k="Application status" v={data.application.status}/>
        </div>
      )}
      <div className="bg-white border border-stone-200 rounded-2xl p-3">
        <div className="text-[10px] uppercase font-black text-stone-500 mb-1">Transactional</div>
        <Row k="Contributions" v={rupees(data.transactional.contributions_paise)}/>
        <Row k="Payouts" v={rupees(data.transactional.payouts_paise)}/>
        <Row k="Fees" v={rupees(data.transactional.fees_paise)}/>
        <Row k="Active pools" v={data.transactional.active_pools}/>
        <Row k="Historical pools" v={data.transactional.history_pools}/>
      </div>
      <div className="bg-white border border-stone-200 rounded-2xl p-3">
        <div className="text-[10px] uppercase font-black text-stone-500 mb-1">Leverage</div>
        <div className="text-[11px] text-stone-600">{data.leverage.note}</div>
      </div>
      <div className="bg-white border border-stone-200 rounded-2xl p-3">
        <div className="text-[10px] uppercase font-black text-stone-500 mb-1">Earnings</div>
        <div className="text-[11px] text-stone-600">{data.earnings.note}</div>
      </div>
      <div className="text-[10px] italic text-stone-500">{data.disclaimer}</div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-center justify-between text-[11px] py-0.5">
      <div className="text-stone-500">{k}</div>
      <div className="font-black text-stone-900 text-right truncate ml-2">{v ?? "—"}</div>
    </div>
  );
}

/* ------------------------------------------------------------- Stats */
function StatsTab() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/efp/statistics").then((r) => setS(r.data)).catch(() => {}); }, []);
  if (!s) return <Loader/>;
  return (
    <div className="space-y-2" data-testid="efp-stats">
      <div className="grid grid-cols-2 gap-2">
        <StatCard label="Total pooled" value={rupees(s.total_pooled_paise)}/>
        <StatCard label="Verified users" value={s.verified_users}/>
        <StatCard label="Total pools" value={s.total_pools}/>
        <StatCard label="Active pools" value={s.active_pools}/>
        <StatCard label="Completed pools" value={s.completed_pools}/>
        <StatCard label="Cities covered" value={s.cities_covered}/>
      </div>
      <div className="text-[10px] italic text-stone-500">{s.disclaimer}</div>
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="bg-white border border-stone-200 rounded-xl p-3">
      <div className="text-[9px] uppercase font-black text-stone-500">{label}</div>
      <div className="font-black text-lg text-stone-900">{value}</div>
    </div>
  );
}

/* ------------------------------------------------------------- Admin (pool types + pools) */
function AdminTab() {
  const [types, setTypes] = useState([]);
  const [allowed, setAllowed] = useState(true);
  const [tab, setTab] = useState("types");
  const load = useCallback(async () => {
    try {
      const r = await api.get("/efp/pool-types");
      setTypes(r.data.items || []);
      setAllowed(true);
    } catch (e) {
      // 403 for non-admin listing? actually list allows current_user; only create is admin
      setAllowed(true);
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  const createType = async () => {
    const name = window.prompt("Pool type name (e.g., 'Elite 10k Monthly'):");
    if (!name) return;
    const contribution = Number(window.prompt("Contribution amount (₹):", "10000") || 0);
    if (!contribution) return;
    const cycle = window.prompt("Cycle (weekly/monthly/quarterly):", "monthly") || "monthly";
    const total_members = Number(window.prompt("Total members:", "10") || 0);
    if (!total_members) return;
    const total_cycles = Number(window.prompt("Total cycles (slots):", "10") || 0);
    if (!total_cycles) return;
    const fee = Number(window.prompt("Fee amount (₹, 0 for none):", "0") || 0);
    try {
      await api.post("/efp/pool-types", {
        name, contribution_paise: contribution * 100, cycle,
        total_members, total_cycles, fee_paise: fee * 100,
      });
      toast.success("Pool type created");
      load();
    } catch (e) {
      setAllowed(false);
      toast.error(e?.response?.data?.detail || "Admin only");
    }
  };
  const createPool = async (pool_type_id, name) => {
    const custom = window.prompt(`Pool name (using type: ${name}):`, name);
    if (!custom) return;
    try {
      await api.post("/efp/pools", { pool_type_id, name: custom });
      toast.success("Pool created");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Admin only");
    }
  };
  return (
    <div className="space-y-2" data-testid="efp-admin">
      <div className="flex gap-1">
        {["types", "pools"].map((t) => (
          <button key={t} onClick={() => setTab(t)}
                  className={`px-2.5 py-1 rounded-full text-[11px] font-black uppercase ${
                    tab === t ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-600"}`}>
            {t}
          </button>
        ))}
        <button onClick={createType} data-testid="efp-admin-new-type"
                className="ml-auto px-2.5 py-1 rounded-full bg-amber-600 text-white text-[11px] font-black uppercase">
          <Plus size={11} className="inline mr-0.5"/> Pool type
        </button>
      </div>
      {tab === "types" && (
        <>
          {types.length === 0 && (
            <div className="p-4 text-center text-stone-500 text-xs italic bg-white border border-stone-200 rounded-xl">
              No pool types configured yet. Only admins can create them.
            </div>
          )}
          {types.map((t) => (
            <div key={t.pool_type_id} className="bg-white border border-stone-200 rounded-2xl p-3"
                 data-testid={`efp-type-${t.pool_type_id}`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-black text-sm">{t.name}</div>
                  <div className="text-[10px] text-stone-500">{rupees(t.contribution_paise)} · {t.cycle} · {t.total_members} members · {t.total_cycles} cycles</div>
                </div>
                <span className={`text-[9px] font-black px-1.5 py-0.5 rounded-full text-white ${
                  t.status === "active" ? "bg-emerald-600" : "bg-stone-500"}`}>{t.status}</span>
              </div>
              {t.status === "active" && (
                <button onClick={() => createPool(t.pool_type_id, t.name)}
                        data-testid={`efp-open-pool-${t.pool_type_id}`}
                        className="mt-2 text-[11px] font-black text-indigo-600">
                  Open new pool from this type →
                </button>
              )}
            </div>
          ))}
        </>
      )}
      {tab === "pools" && <AdminPoolsList/>}
      {!allowed && (
        <div className="text-[10px] italic text-amber-700">Admin-only actions will fail if you are not an admin.</div>
      )}
    </div>
  );
}

function AdminPoolsList() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(null);
  const load = useCallback(async () => {
    // reuse explore endpoint for admin view (open pools). For richer admin scope,
    // Slice-2 will add /efp/admin/pools with full status filters.
    try {
      const r = await api.get("/efp/pools/explore");
      setItems(r.data.items || []);
    } catch { /* ignored */ }
  }, []);
  useEffect(() => { load(); }, [load]);
  const doAction = async (pool_id, action) => {
    const reason = window.prompt(`Reason (optional) for ${action}:`) || undefined;
    setBusy(pool_id + action);
    try {
      await api.post(`/efp/pools/${pool_id}/${action}`, { reason });
      toast.success(`Pool ${action}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed (admin only)");
    } finally { setBusy(null); }
  };
  if (items.length === 0) return <div className="p-4 text-center text-stone-500 text-xs italic bg-white border border-stone-200 rounded-xl">No open pools right now.</div>;
  return (
    <>
      {items.map((p) => (
        <div key={p.pool_id} className="bg-white border border-stone-200 rounded-2xl p-3">
          <div className="font-black text-sm">{p.name}</div>
          <div className="text-[10px] text-stone-500">{p.pool_type_name} · {p.member_count}/{p.total_members}</div>
          <div className="flex gap-1 mt-2">
            {["close", "cancel", "default"].map((a) => (
              <button key={a} onClick={() => doAction(p.pool_id, a)} disabled={busy === p.pool_id + a}
                      data-testid={`efp-pool-${a}-${p.pool_id}`}
                      className="px-2.5 py-1 rounded-full text-[10px] font-black uppercase bg-stone-900 text-white disabled:opacity-50">
                {a}
              </button>
            ))}
          </div>
        </div>
      ))}
    </>
  );
}

/* ------------------------------------------------------------- More / Coming Soon */
function MoreTab() {
  const [about, setAbout] = useState(null);
  useEffect(() => { api.get("/efp/help/about").then((r) => setAbout(r.data)).catch(() => {}); }, []);
  return (
    <div className="space-y-2" data-testid="efp-more">
      {about && (
        <div className="bg-white border border-stone-200 rounded-2xl p-3">
          <div className="text-[10px] uppercase font-black text-stone-500">What is EFP?</div>
          <div className="text-[12px] text-stone-800 mt-1">{about.body}</div>
          <ul className="mt-2 text-[11px] text-stone-600 list-disc list-inside">
            {about.principles.map((p, i) => <li key={i}>{p}</li>)}
          </ul>
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        {COMING_SOON.map((c) => {
          const Ic = c.icon;
          return (
            <div key={c.label} className="bg-white border border-stone-200 rounded-2xl p-3"
                 data-testid={`efp-cs-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              <Ic size={16} className="text-amber-700"/>
              <div className="text-[12px] font-black text-stone-900 mt-1">{c.label}</div>
              <div className="text-[10px] uppercase font-black text-amber-700 mt-0.5">Coming soon</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------- misc */
function Loader() {
  return <div className="py-8 text-center text-stone-400"><Loader2 size={16} className="animate-spin inline mr-1"/> Loading…</div>;
}

function NeedLogin() {
  return (
    <div className="bg-white border border-stone-200 rounded-2xl p-4 text-center" data-testid="efp-need-login">
      <div className="text-[10px] uppercase font-black text-stone-500">EFP · Elite Fund Pool</div>
      <div className="font-black text-base mt-1">Sign in to continue</div>
      <div className="text-[11px] text-stone-600 mt-1">EFP reuses your Find2near identity and KYC — no duplicate signup.</div>
      <Link to="/" className="inline-block mt-3 px-4 py-2 rounded-full bg-amber-600 text-white font-black text-xs">
        Go to sign in
      </Link>
    </div>
  );
}
